import React, { useState, useEffect } from "react";
const BASE_URL = process.env.REACT_APP_API_URL || "";

function useScript(src, id) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (document.getElementById(id)) { setReady(true); return; }
    const s = document.createElement("script");
    s.src = src; s.id = id; s.async = true;
    s.onload = () => setReady(true);
    document.head.appendChild(s);
  }, [src, id]);
  return ready;
}

function ErrorBox({ m }) {
  return <div style={{ background:"rgba(255,107,107,0.08)", border:"1px solid rgba(255,107,107,0.25)",
    borderRadius:8, padding:"10px 14px", color:"var(--rose)", fontSize:13,
    display:"flex", gap:8 }}>❌ {m}</div>;
}
function SuccessBox({ m }) {
  return <div style={{ background:"rgba(81,207,102,0.08)", border:"1px solid rgba(81,207,102,0.25)",
    borderRadius:8, padding:"10px 14px", color:"var(--green)", fontSize:13,
    display:"flex", gap:8 }}>✅ {m}</div>;
}
function Spinner() {
  return <span style={{ display:"inline-block", animation:"spin 0.8s linear infinite" }}>⟳</span>;
}

export default function Login({ onLogin }) {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ username:"", password:"", confirmPassword:"", adminCode:"" });
  const [loading, setLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [shake, setShake] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [showAdminCode, setShowAdminCode] = useState(false);

  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
  const FACEBOOK_APP_ID  = process.env.REACT_APP_FACEBOOK_APP_ID  || "";

  const googleReady   = useScript("https://accounts.google.com/gsi/client", "google-gsi");
  const facebookReady = useScript("https://connect.facebook.net/en_US/sdk.js", "fb-sdk");

  useEffect(() => {
    if (!googleReady || !GOOGLE_CLIENT_ID || !window.google) return;
    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: handleGoogleResponse,
      auto_select: false,
    });
  }, [googleReady, GOOGLE_CLIENT_ID]);

  useEffect(() => {
    if (!facebookReady || !FACEBOOK_APP_ID || !window.FB) return;
    window.FB.init({ appId: FACEBOOK_APP_ID, cookie: true, xfbml: true, version: "v19.0" });
  }, [facebookReady, FACEBOOK_APP_ID]);

  const triggerShake = () => { setShake(true); setTimeout(() => setShake(false), 600); };
  const handleChange = (e) => { setForm(p => ({ ...p, [e.target.name]: e.target.value })); setError(""); };

  const handleSocialAuth = async ({ provider, token, name, email }) => {
    setSocialLoading(provider); setError("");
    try {
      const res  = await fetch(`${BASE_URL}/api/auth/social`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ provider, token, name, email }),
      });
      const data = await res.json();
      if (data.success) {
        sessionStorage.setItem("st_token", data.token);
        sessionStorage.setItem("st_username", data.username);
        sessionStorage.setItem("st_role", data.role);
        onLogin(data.token, data.username, data.role);
      } else { setError(data.message || "Social login failed."); triggerShake(); }
    } catch { setError("Cannot connect to server."); triggerShake(); }
    finally { setSocialLoading(""); }
  };

  const handleGoogleResponse = async (response) => {
    if (!response?.credential) { setError("Google sign-in was cancelled."); return; }
    try {
      const payload = JSON.parse(atob(response.credential.split(".")[1]));
      await handleSocialAuth({ provider:"google", token:response.credential, name:payload.name, email:payload.email });
    } catch { setError("Could not process Google sign-in."); }
  };

  const handleGoogleClick = () => {
    if (!GOOGLE_CLIENT_ID) { setError("Google login not configured. Set REACT_APP_GOOGLE_CLIENT_ID in your frontend .env"); return; }
    window.google?.accounts?.id?.prompt();
  };

  const handleFacebookClick = () => {
    if (!FACEBOOK_APP_ID) { setError("Facebook login not configured. Set REACT_APP_FACEBOOK_APP_ID in your frontend .env"); return; }
    if (!window.FB) { setError("Facebook SDK not loaded yet. Please try again."); return; }
    setSocialLoading("facebook");
    window.FB.login((response) => {
      if (response.authResponse) {
        const { accessToken } = response.authResponse;
        window.FB.api("/me", { fields:"id,name,email" }, async (user) => {
          await handleSocialAuth({ provider:"facebook", token:accessToken, name:user.name, email:user.email || `fb_${user.id}@facebook.com` });
        });
      } else { setSocialLoading(""); setError("Facebook sign-in was cancelled."); }
    }, { scope:"public_profile,email" });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!form.username || !form.password) { setError("Please enter your username and password."); triggerShake(); return; }
    setLoading(true); setError("");
    try {
      const res  = await fetch(`${BASE_URL}/api/auth/login`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ username:form.username, password:form.password }),
      });
      const data = await res.json();
      if (data.success) {
        sessionStorage.setItem("st_token", data.token);
        sessionStorage.setItem("st_username", data.username);
        sessionStorage.setItem("st_role", data.role);
        onLogin(data.token, data.username, data.role);
      } else { setError(data.message || "Login failed."); triggerShake(); }
    } catch { setError("Cannot connect to server."); triggerShake(); }
    finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!form.username || !form.password || !form.confirmPassword) { setError("Fill in all fields."); triggerShake(); return; }
    if (form.password !== form.confirmPassword) { setError("Passwords do not match."); triggerShake(); return; }
    if (form.password.length < 6) { setError("Password must be at least 6 characters."); triggerShake(); return; }
    setLoading(true); setError(""); setSuccess("");
    try {
      const res  = await fetch(`${BASE_URL}/api/auth/register`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ username:form.username, password:form.password, confirmPassword:form.confirmPassword, adminCode:form.adminCode }),
      });
      const data = await res.json();
      if (data.success) {
        sessionStorage.setItem("st_token", data.token);
        sessionStorage.setItem("st_username", data.username);
        sessionStorage.setItem("st_role", data.role);
        setSuccess(data.message);
        setTimeout(() => onLogin(data.token, data.username, data.role), 700);
      } else { setError(data.message || "Registration failed."); triggerShake(); }
    } catch { setError("Cannot connect to server."); triggerShake(); }
    finally { setLoading(false); }
  };

  const switchMode = (m) => { setMode(m); setError(""); setSuccess(""); setForm({ username:"", password:"", confirmPassword:"", adminCode:"" }); };
  const disabled   = !!socialLoading || !!loading;

  return (
    <div style={{ minHeight:"100vh", background:"var(--bg)", display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
      <div style={{ position:"fixed", top:"5%", left:"50%", transform:"translateX(-50%)", width:700, height:400, borderRadius:"50%", pointerEvents:"none", background:"radial-gradient(ellipse, rgba(212,168,83,0.07) 0%, transparent 70%)" }} />
      <div style={{ width:"100%", maxWidth:440, position:"relative", zIndex:1 }}>

        {/* Logo */}
        <div style={{ textAlign:"center", marginBottom:32 }}>
          <div style={{ width:68, height:68, borderRadius:20, margin:"0 auto 16px", background:"linear-gradient(135deg,#d4a853,#f0c060 50%,#b8922e)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:32, boxShadow:"0 8px 32px rgba(212,168,83,0.4)", animation:"float 4s ease-in-out infinite" }}>🏠</div>
          <h1 style={{ fontSize:32, fontFamily: "Inter, sans-serif", fontWeight:800, letterSpacing:"-0.03em", marginBottom:6 }} className="text-gold">StayTrack</h1>
          <p style={{ color:"var(--text-2)", fontSize:14 }}>{mode==="login" ? "Sign in to your workspace" : "Create your account"}</p>
        </div>

        {/* Tabs */}
        <div style={{ display:"flex", background:"rgba(255,255,255,0.03)", border:"1px solid var(--border)", borderRadius:14, padding:4, marginBottom:20 }}>
          {[["login","Sign In"],["register","Create Account"]].map(([k,l]) => (
            <button key={k} onClick={() => switchMode(k)} style={{ flex:1, padding:"11px", fontSize:13, fontWeight:700,
              background: mode===k ? "linear-gradient(135deg,#d4a853,#b8922e)" : "transparent",
              border:"none", borderRadius:10, color: mode===k ? "#0a0a12" : "var(--text-3)",
              cursor:"pointer", transition:"all 0.2s", fontFamily: "Inter, sans-serif",
              boxShadow: mode===k ? "0 4px 14px rgba(212,168,83,0.3)" : "none" }}>{l}</button>
          ))}
        </div>

        {/* Card */}
        <div style={{ background:"var(--card)", backdropFilter:"blur(20px)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:"20px", padding:"28px 28px 24px", boxShadow:"0 24px 64px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)", animation: shake ? "shakeX 0.5s ease" : "fadeIn 0.35s var(--ease)" }}>

          {/* Social buttons */}
          <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:20 }}>
            <button onClick={handleGoogleClick} disabled={disabled}
              style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:10, width:"100%", padding:"11px 16px", background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:"10px", cursor:"pointer", fontFamily: "Inter, sans-serif", fontSize:14, fontWeight:600, color:"var(--text)", opacity:disabled?0.6:1, transition:"all 0.15s" }}
              onMouseEnter={e=>{if(!disabled){e.currentTarget.style.background="rgba(255,255,255,0.1)";e.currentTarget.style.borderColor="rgba(255,255,255,0.22)";}}}
              onMouseLeave={e=>{e.currentTarget.style.background="rgba(255,255,255,0.06)";e.currentTarget.style.borderColor="rgba(255,255,255,0.12)";}}>
              {socialLoading==="google" ? <Spinner /> : (
                <svg width="18" height="18" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              )}
              Continue with Google
            </button>

            <button onClick={handleFacebookClick} disabled={disabled}
              style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:10, width:"100%", padding:"11px 16px", background:"rgba(24,119,242,0.12)", border:"1px solid rgba(24,119,242,0.3)", borderRadius:"10px", cursor:"pointer", fontFamily: "Inter, sans-serif", fontSize:14, fontWeight:600, color:"#4a9ff5", opacity:disabled?0.6:1, transition:"all 0.15s" }}
              onMouseEnter={e=>{if(!disabled)e.currentTarget.style.background="rgba(24,119,242,0.22)";}}
              onMouseLeave={e=>{e.currentTarget.style.background="rgba(24,119,242,0.12)";}}>
              {socialLoading==="facebook" ? <Spinner /> : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="#1877F2">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              )}
              Continue with Facebook
            </button>
          </div>

          <div className="divider" style={{ marginBottom:20 }}>or continue with email</div>

          {/* Email forms */}
          {mode === "login" ? (
            <form onSubmit={handleLogin}>
              <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                <div className="form-group">
                  <label>Username</label>
                  <input name="username" autoComplete="username" placeholder="Enter your username" value={form.username} onChange={handleChange} autoFocus style={{ fontSize:15 }} />
                </div>
                <div className="form-group">
                  <label>Password</label>
                  <div style={{ position:"relative" }}>
                    <input name="password" type={showPass?"text":"password"} autoComplete="current-password" placeholder="Enter your password" value={form.password} onChange={handleChange} style={{ fontSize:15, paddingRight:42 }} />
                    <button type="button" onClick={()=>setShowPass(p=>!p)} style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", color:"var(--text-3)", cursor:"pointer", fontSize:16, padding:0 }}>{showPass?"🙈":"👁️"}</button>
                  </div>
                </div>
                {error && <ErrorBox m={error} />}
                <button type="submit" className="st-btn st-btn-primary" disabled={disabled} style={{ padding:"13px", fontSize:15, marginTop:2 }}>{loading ? <Spinner /> : "Sign In →"}</button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleRegister}>
              <div style={{ display:"flex", flexDirection:"column", gap:13 }}>
                <div className="form-group">
                  <label>Username *</label>
                  <input name="username" autoComplete="username" placeholder="letters, numbers and _" value={form.username} onChange={handleChange} autoFocus style={{ fontSize:15 }} />
                </div>
                <div className="form-group">
                  <label>Password *</label>
                  <div style={{ position:"relative" }}>
                    <input name="password" type={showPass?"text":"password"} autoComplete="new-password" placeholder="At least 6 characters" value={form.password} onChange={handleChange} style={{ fontSize:15, paddingRight:42 }} />
                    <button type="button" onClick={()=>setShowPass(p=>!p)} style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", color:"var(--text-3)", cursor:"pointer", fontSize:16, padding:0 }}>{showPass?"🙈":"👁️"}</button>
                  </div>
                </div>
                <div className="form-group">
                  <label>Confirm Password *</label>
                  <input name="confirmPassword" type={showPass?"text":"password"} autoComplete="new-password" placeholder="Re-enter your password" value={form.confirmPassword} onChange={handleChange} style={{ fontSize:15 }} />
                </div>
                <div>
                  <button type="button" onClick={()=>setShowAdminCode(p=>!p)} style={{ background:"none", border:"none", cursor:"pointer", color:"var(--text-3)", fontSize:12, padding:0, display:"flex", alignItems:"center", gap:6, fontFamily: "Inter, sans-serif" }}>
                    {showAdminCode?"▼":"▶"} Have an admin code? (optional)
                  </button>
                  {showAdminCode && <div className="form-group" style={{ marginTop:8 }}><input name="adminCode" type="password" placeholder="Enter admin code" value={form.adminCode} onChange={handleChange} /></div>}
                </div>
                {error   && <ErrorBox m={error} />}
                {success && <SuccessBox m={success} />}
                <button type="submit" className="st-btn st-btn-primary" disabled={disabled} style={{ padding:"13px", fontSize:15, marginTop:2 }}>{loading ? <Spinner /> : "✨ Create Account"}</button>
              </div>
            </form>
          )}
        </div>

        <div style={{ marginTop:14, padding:"12px 16px", background:"rgba(255,255,255,0.02)", borderRadius:10, border:"1px solid var(--border)", fontSize:12, color:"var(--text-3)", textAlign:"center" }}>
          {mode==="login" ? "👑 First account created is automatically the admin." : <>🔒 Admin code is set via <code style={{ color:"var(--gold-500)" }}>ADMIN_CODE</code> in your backend .env</>}
        </div>
      </div>

      <style>{`
        @keyframes shakeX { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-10px)} 40%{transform:translateX(10px)} 60%{transform:translateX(-8px)} 80%{transform:translateX(8px)} }
        @keyframes float  { 0%,100%{transform:translateY(0) rotate(-2deg)} 50%{transform:translateY(-8px) rotate(2deg)} }
      `}</style>
    </div>
  );
}
